// Need to use the React-specific entry point to import createApi
import { createSelector } from '@reduxjs/toolkit';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import { camelizeKeys } from 'humps';
import { RootState } from 'stores';
import { Issue, KioskPublicationCategory, providesListWithId } from './types';


export const API_KEY = process.env.REACT_APP_API_SECRETE!
export const API_BASE_URL = process.env.REACT_APP_API_URL!

export interface AuthState {
  email?: string | null;
  password?: string | null;
}

export interface AuthPayLoad {
  refreshToken: string;
  id: number;
  token: string;
}

const transformIssue = (publicationNumber: any, publication?: any): Issue => ({
  ...publicationNumber,
  id: publicationNumber.id,
  name: publicationNumber?.publicationName || publication?.name,
  publicationId: publicationNumber?.publicationId || publication?.id,
  publicationName: publicationNumber?.publicationName || publication?.name,
  countPages: publicationNumber?.countPages,
  coverUrl: publicationNumber.coverUrl,
  // pdfUrl: publicationNumber.pdfUrl,
  date: publicationNumber.publishedAt,
  number: publicationNumber.number,
  ojd: publicationNumber.ojd ? {
    ...publicationNumber.ojd,
  } : null,
  publicationNumber: publicationNumber
})
const transformIssues = (data: any[]) => {
  return data.map(transformIssue)
}

const baseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  prepareHeaders: (headers, { getState, }) => {
    const token = (getState() as RootState)?.auth?.token

    headers.set('locale', 'fr')
    headers.set('X-Accept-Version', '3')

    // If we have a token set in state, let's assume that we should be passing it.
    if (token) {
      headers.set('authorization', `${token}`)
    } else {
      headers.set('authorization', `${API_KEY}`)
    }

    return headers
  },

})

export const api = createApi({
  reducerPath: 'api',
  refetchOnReconnect: true,
  tagTypes: ['Profile', 'Kiosk', 'UserPublication', 'KioskCategory'],

  baseQuery: baseQuery,
  endpoints: (builder) => ({
    signIn: builder.mutation<AuthPayLoad, AuthState | void>({
      query: (data) => ({
        url: '/authentication',
        method: 'POST',
        body: {
          kiosk: process.env.REACT_APP_KIOSK_ID,
          ...data
        }
      }),
    }),
    checkAvailability: builder.mutation<{ available: boolean }, string>({
      query: (code) => ({
        url: `/kiosk_access_codes/${code}/available`,
        method: 'POST',
      }),
    }),
    codeToken: builder.mutation<{ token: string }, string>({
      query: (code) => ({
        url: `/kiosk_access_codes/token`,
        method: 'POST',
        body: {
          code,
          kiosk: process.env.REACT_APP_KIOSK_ID
        },
      }),
    }),
    categories: builder.query<KioskPublicationCategory[], void>({
      query: () => ({
        url: '/kiosk/categories',
        method: 'GET',
        param: {},
      }),
      transformResponse: (baseQueryReturnValue: KioskPublicationCategory[]) => camelizeKeys(baseQueryReturnValue) as any,
    }),
    issues: builder.query<Issue[], void>({
      query: () => ({
        url: '/kiosk/issues',
        method: 'GET',
        param: {},
      }),
      transformResponse: (baseQueryReturnValue: KioskPublicationCategory[]) => camelizeKeys(baseQueryReturnValue) as any,
    }),
    lastUpdatedissues: builder.query<Issue[], void>({
      query: () => ({
        url: '/kiosk/publication_issues',
        method: 'GET',
        param: {
          last_updated: true
        },
      }),
      // transformResponse: (baseQueryReturnValue: KioskPublicationCategory[]) => camelizeKeys(baseQueryReturnValue) as any,

      providesTags: (result) => [...providesListWithId(result, 'Kiosk'), 'Profile'],
      transformResponse: (response: any) => camelizeKeys(transformIssues(response?.results) || response) as any
    }),
    kioskAccessCodes: builder.mutation<{ url: string, code: string }, void>({
      query: () => ({
        url: '/kiosk_access_codes',
        method: 'POST',
        param: {
          kiosk: process.env.REACT_APP_KIOSK_ID
        },
        body: {
          kiosk: process.env.REACT_APP_KIOSK_ID,
        }
      }),
    }),
    saveRhoneInfo: builder.mutation<void, any>({
      query: (data) => ({
        url: `/rhonexpress`,
        method: 'POST',
        body: data,
      }),
    }),
  }),
})

export const selectKioskSections = createSelector(api.endpoints.categories.select(), result => result.data ?? [])
export const selectAllIssues = createSelector(selectKioskSections, sections => sections.map(({ items }) => items).flat())

export const {
  useSignInMutation,
  useCheckAvailabilityMutation,
  useCodeTokenMutation,
  useCategoriesQuery,
  useKioskAccessCodesMutation,
  useIssuesQuery,
  useLastUpdatedissuesQuery,
  useSaveRhoneInfoMutation,
} = api